import Dexie, { type Table } from 'dexie';
import type { BlobJob, ProjectSync, TrashEntry } from '../sync/types.ts';
import type { ProjectFile } from './record.ts';

/** Key/value rows (settings, misc device-local preferences). */
export interface KvRow {
  key: string;
  value: unknown;
}

/** Blob rows: the IndexedDB fallback when OPFS is unavailable (keyed by name, e.g. 'blobs/<sha>'). */
export interface BlobRow {
  key: string;
  blob: Blob;
}

/**
 * IndexedDB schema (Dexie). Project documents are stored exactly as the shared project JSON
 * (contracts/schemas/project.schema.json, schemaVersion 1) keyed by id; unknown keys are kept.
 * Later (phase B) sync metadata becomes extra columns on the same rows (06 §1.4).
 */
export class BetterLightsDB extends Dexie {
  projects!: Table<ProjectFile, string>;
  kv!: Table<KvRow, string>;
  blobs!: Table<BlobRow, string>;
  /** Sync sidecar per project (never part of the pushed document). */
  projectSync!: Table<ProjectSync, string>;
  /** Offline blob upload queue. */
  blobJobs!: Table<BlobJob, string>;
  /** Server tombstones the user can restore (30 days). */
  trash!: Table<TrashEntry, string>;

  constructor(name = 'better-lights') {
    super(name);
    this.version(1).stores({
      projects: 'id, updatedAt',
      kv: 'key',
      blobs: 'key',
    });
    this.version(2).stores({
      projectSync: 'id, userId, scope, dirty',
      blobJobs: 'key, userId, scope, nextAt',
      trash: 'id, userId, scope',
    });
  }
}

let instance: BetterLightsDB | null = null;

export function db(): BetterLightsDB {
  instance ??= new BetterLightsDB();
  return instance;
}

/** Tests: use a fresh database. */
export function setDb(next: BetterLightsDB): void {
  instance = next;
}
